import React, { useState, useEffect, useRef } from 'react';
import { chatAPI } from '../lib/api';
import { Button } from './ui/button';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';

export function LiveChatWidget() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const bottomRef = useRef();

  useEffect(() => {
    if (open && !sessionId) startSession();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const startSession = async () => {
    setStarting(true);
    try {
      const r = await chatAPI.createSession({ visitor_name: 'Visitor' });
      setSessionId(r.data.id);
      setMessages([{
        sender: 'bot',
        content: 'Hello! Welcome to Neural News. How can I help you today?',
        ts: new Date().toISOString(),
        role: 'bot',
      }]);
    } catch {
      setMessages([{ sender: 'bot', content: 'Chat is currently unavailable. Please try later.', ts: new Date().toISOString(), role: 'bot' }]);
    } finally { setStarting(false); }
  };

  const sendMessage = async () => {
    if (!input.trim() || !sessionId || loading) return;
    const userMsg = { sender: 'user', content: input, ts: new Date().toISOString(), role: 'user' };
    setMessages(prev => [...prev, userMsg]);
    const text = input;
    setInput('');
    setLoading(true);
    try {
      const r = await chatAPI.sendMessage(sessionId, { content: text, ai_assist: true });
      if (r.data.bot_reply) {
        setMessages(prev => [...prev, r.data.bot_reply]);
      }
    } catch {
      setMessages(prev => [...prev, { sender: 'bot', content: 'Sorry, something went wrong. Please try again.', ts: new Date().toISOString(), role: 'bot' }]);
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {open ? (
        <div className="w-80 bg-white rounded-2xl shadow-2xl border border-[#E7E0D6] flex flex-col overflow-hidden" style={{ height: 420 }}>
          <div className="bg-[#121316] text-white px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              <span className="text-sm font-medium">Neural News Support</span>
            </div>
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-white hover:bg-white/10" onClick={() => setOpen(false)}>
              <X className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-[#FBF7EF]">
            {starting ? (
              <div className="flex justify-center pt-8"><Loader2 className="w-5 h-5 animate-spin text-[#B89B5E]" /></div>
            ) : messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm ${m.role === 'user' ? 'bg-[#121316] text-white rounded-br-sm' : 'bg-white text-[#121316] border border-[#E7E0D6] rounded-bl-sm'}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-[#E7E0D6] px-3 py-2 rounded-2xl rounded-bl-sm">
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-[#5B616E]" />
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>
          <div className="p-3 border-t border-[#E7E0D6] bg-white flex-shrink-0">
            <div className="flex gap-2">
              <input
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && sendMessage()}
                placeholder="Type a message..."
                className="flex-1 border border-[#E7E0D6] rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#B89B5E]"
              />
              <Button size="sm" onClick={sendMessage} disabled={!input.trim() || loading} className="bg-[#121316] text-white hover:bg-[#1B1D22] h-9 w-9 p-0 rounded-xl">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <Button
          onClick={() => setOpen(true)}
          className="w-14 h-14 rounded-full bg-[#121316] text-white hover:bg-[#1B1D22] shadow-2xl p-0"
        >
          <MessageCircle className="w-6 h-6" />
        </Button>
      )}
    </div>
  );
}
